import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleThunkError } from '../common/util';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { RootState } from '.';
import { ITypeCode, IDelayCodeSyncStatus } from '../models/dms/typecode.model';
import { IOdataResult } from '../models/odata.model';
import { setIsLoadingCountState } from './appSlice';
import { getTypecodesByType } from '../api/dms/typecode.api';
import { logConsole, logTransaction } from '../common/log';
import { LogType } from '../models/log.model';

interface IDelayCodeSyncState {
  delayCodesSync?: IDelayCodeSyncStatus;
  delayCodesSyncLoading: boolean;
}

const initialState: IDelayCodeSyncState = {
  delayCodesSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: {} as IOdataResult<ITypeCode>
  },
  delayCodesSyncLoading: false,
};

/// THUNKS

export const setDelayCodesSyncStoreAsync = createAsyncThunk<{}, IDelayCodeSyncStatus | undefined>(
  'delayCodeSync/setDelayCodesSyncStoreAsync',
  async (val, { rejectWithValue, dispatch }) => {
    logTransaction(LogType.Info, `Setting delay codes to storage`, '[delayCodeSync] setDelayCodesSyncStoreAsync');
    try {
      dispatch(setDelayCodesSyncState(val));
      await updateConfigObject<IDelayCodeSyncStatus>(ConfigName.DelayCodesSync, val || {} as IDelayCodeSyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setDelayCodesProcessingStoreAsync = createAsyncThunk<{}, boolean>(
  'delayCodeSync/setDelayCodesProcessingAsync',
  async (val, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[delayCodeSync] setDelayCodesProcessingAsync');
    try {
      dispatch(setDelayCodesProcessingState(val));
      await updateConfigObject<IDelayCodeSyncStatus>(ConfigName.DelayCodesSync, {
        ...(getState() as RootState).delayCodeSync.delayCodesSync,
        isProcessing: val
      } as IDelayCodeSyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const setDelayCodesHasSyncStoreAsync = createAsyncThunk<{}, boolean>(
  'delayCodeSync/setDelayCodesHasSyncStoreAsync',
  async (val, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, '', '[delayCodeSync] setDelayCodesHasSyncStoreAsync');
    try {
      dispatch(setDelayCodesHasSyncState(val));
      await updateConfigObject<IDelayCodeSyncStatus>(ConfigName.DelayCodesSync, {
        ...(getState() as RootState).delayCodeSync.delayCodesSync,
        hasSync: val,
        isProcessing: false,
      } as IDelayCodeSyncStatus, false);
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  }
);

export const fetchDelayCodesStoreAsync = createAsyncThunk<{}, void>(
  'delayCodeSync/fetchDelayCodesStoreAsync',
  async (nil, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Fetching delay codes from database`, '[delayCodeSync] fetchDelayCodesStoreAsync');
    try {
      dispatch(setIsLoadingCountState(1));
      dispatch(setDelayCodesSyncLoadingState(true));

      let delayCodesSyncState = { ...(getState() as RootState).delayCodeSync.delayCodesSync } as IDelayCodeSyncStatus;

      try {
        const responseData = await getTypecodesByType('DY');

        delayCodesSyncState = {
          ...delayCodesSyncState,
          timestamp: new Date().getTime(),
          hasSync: true,
          isProcessing: false,
          data: { ...responseData }
        };

        dispatch(setDelayCodesSyncState(delayCodesSyncState));
        await updateConfigObject<IDelayCodeSyncStatus>(ConfigName.DelayCodesSync, delayCodesSyncState, false);
        return responseData;
      } catch (ex: any) {
        logTransaction(LogType.Error, `Failed to execute fetchDelayCodesStoreAsync thunk. ${ex?.message}`);
      } finally {
        dispatch(setIsLoadingCountState(-1));
        dispatch(setDelayCodesSyncLoadingState(false));
      }

    } catch (err: any) {
      return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setDelayCodesSyncLoadingState(false));
    }
  }
);

/// SLICE

export const delayCodeSyncSlice = createSlice({
  name: 'delayCodeSync',
  initialState,
  reducers: {
    setDelayCodesSyncState: (state, action: PayloadAction<IDelayCodeSyncStatus | undefined>) => {
      logConsole(LogType.Info, '[delayCodeSync] setDelayCodesSyncState');
      state.delayCodesSync = action.payload;
    },
    setDelayCodesProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[delayCodeSync] setDelayCodesProcessingState: ${action.payload}`);
      if (state.delayCodesSync) {
        state.delayCodesSync.isProcessing = action.payload;
      }
    },
    setDelayCodesHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[delayCodeSync] setDelayCodesHasSyncState: ${action.payload}`);
      if (state.delayCodesSync) {
        state.delayCodesSync.hasSync = action.payload;
        state.delayCodesSync.isProcessing = false;
      }
    },
    setDelayCodesSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[delayCodeSync] setDelayCodesSyncLoadingState: ${action.payload}`);
      state.delayCodesSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const {
  setDelayCodesSyncState,
  setDelayCodesProcessingState,
  setDelayCodesHasSyncState,
  setDelayCodesSyncLoadingState, } = delayCodeSyncSlice.actions;

export default delayCodeSyncSlice.reducer;
